// src/features/task/TaskTypeFormPropertiesEditor.tsx
// What the forms for this task type ask for, and in what order.
//
// The lifecycle switches on the card above say what a type is *capable* of.
// They could never say what the person filling the form is asked: there is no
// flag for "a chore has no due date", none for putting the assignee above the
// priority. This editor is where that is stated, and the list it edits is
// `TaskTypeDefinition.form_properties` exactly as the form reads it.
//
// An empty list is not an empty screen. It means the platform default set — the
// whole catalogue, in declaration order — and that is shown, read-only and
// named as such, so "nothing configured" and "nothing offered" never look
// alike. Owning the list is one explicit action, stated before it happens.
//
// A property switched off stays in the list rather than being deleted, because
// its position is worth keeping: switching it back on should put it back where
// the administrator had it, not at the end.
//
// The one combination the service refuses — required together with hidden, a
// form that can never be submitted because nothing on screen can fill the
// property blocking it — is not reachable here at all. Hiding a property clears
// its required flag, and the required switch is disabled while it is hidden.
// A rule enforced by a 400 after the save is a rule the person meets by
// failing; this one they simply cannot break.

import { useMemo, useState } from 'react';
import { useI18n } from '@react/hooks';
import { Badge, Button } from '@react/components';
import { AnchoredMenu } from '@/components/AnchoredMenu';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import type { TaskFormProperty, TaskTypeProperty } from '@core/types/task';
import {
  CREATE_FORM_PROPERTIES,
  FORM_PROPERTY_CATALOGUE,
  catalogueAsRows,
  customFieldSlotKey,
  formPropertyLabelKey,
  formPropertyRowKey,
  moveFormProperty,
  updateFormProperty,
} from '@/features/task/formProperties';

/** Payload of a row drag. Its own type so nothing else's drag lands here. */
const ROW_DRAG_TYPE = 'application/x-corde-task-form-property';

const CHEVRON_UP = 'M18 15l-6-6-6 6';
const CHEVRON_DOWN = 'M6 9l6 6 6-6';
const GRIP = 'M9 6h.01M9 12h.01M9 18h.01M15 6h.01M15 12h.01M15 18h.01';

function Glyph({ path, className }: { path: string; className?: string }) {
  return (
    <svg
      className={className ?? 'h-4 w-4'}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={path} />
    </svg>
  );
}

export interface TaskTypeFormPropertiesEditorProps {
  value: TaskTypeProperty[];
  onChange: (next: TaskTypeProperty[]) => void;
  /** The tenant's custom fields, so one can be added to the form. */
  customFieldOptions: { value: string; label: string }[];
  disabled?: boolean;
}

export function TaskTypeFormPropertiesEditor({
  value,
  onChange,
  customFieldOptions,
  disabled,
}: TaskTypeFormPropertiesEditorProps) {
  const { t } = useI18n();
  const [dropIndex, setDropIndex] = useState<number | null>(null);
  const [addAnchor, setAddAnchor] = useState<DOMRect | null>(null);

  const usedKeys = useMemo(() => new Set(value.map(formPropertyRowKey)), [value]);

  /** Catalogue properties this configuration does not mention yet. */
  const missingProperties = useMemo(
    () => FORM_PROPERTY_CATALOGUE.filter((property) => !usedKeys.has(property)),
    [usedKeys],
  );

  const availableCustomFields = useMemo(
    () => customFieldOptions.filter((option) => !usedKeys.has(customFieldSlotKey(option.value))),
    [customFieldOptions, usedKeys],
  );

  const rowLabel = (row: TaskTypeProperty): string => {
    if (row.custom_field_id) {
      const known = customFieldOptions.find((option) => option.value === row.custom_field_id);
      return known?.label || row.custom_field_id;
    }
    return row.property ? t(formPropertyLabelKey(row.property)) : row.custom_field_id;
  };

  const append = (row: TaskTypeProperty) => {
    // Adding to a type that is still following the default set would read as
    // "the default plus this one" while meaning "only this one", so the default
    // is materialised first and the addition lands at the end of it.
    const base = value.length > 0 ? value : catalogueAsRows();
    onChange([...base, row]);
    setAddAnchor(null);
  };

  const move = (from: number, to: number) => onChange(moveFormProperty(value, from, to));

  // ── Following the default set ────────────────────────────────────────────
  if (value.length === 0) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">{t('task.formProperties.description')}</p>

        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="secondary" size="sm">
            {t('task.formProperties.defaultBadge')}
          </Badge>
          <p className="text-sm font-medium text-foreground">
            {t('task.formProperties.defaultHeading', { count: FORM_PROPERTY_CATALOGUE.length })}
          </p>
        </div>

        {/* Read-only, not disabled: this is what the forms will actually show.
            It simply is not this type's list yet. Nothing inside is focusable,
            so the absence of controls says the same thing to the keyboard. */}
        <ul className="space-y-2" aria-label={t('task.formProperties.defaultListLabel')}>
          {FORM_PROPERTY_CATALOGUE.map((property, index) => (
            <li
              key={property}
              className="flex flex-wrap items-center gap-2 rounded-md border border-dashed border-border bg-muted/30 px-3 py-2"
            >
              <span className="w-5 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
                {index + 1}
              </span>
              <span className="truncate text-sm font-medium text-muted-foreground">
                {t(formPropertyLabelKey(property))}
              </span>
              {!CREATE_FORM_PROPERTIES.has(property) && (
                <span className="text-xs text-muted-foreground/80">
                  {t('task.formProperties.notOnCreate')}
                </span>
              )}
            </li>
          ))}
        </ul>

        <div className="flex flex-wrap items-center gap-3">
          <Button
            size="sm"
            variant="secondary"
            disabled={disabled}
            onClick={() => onChange(catalogueAsRows())}
          >
            {t('task.formProperties.adopt')}
          </Button>
          <p className="text-xs text-muted-foreground">{t('task.formProperties.adoptHint')}</p>
        </div>
      </div>
    );
  }

  // ── The type's own list ──────────────────────────────────────────────────
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">{t('task.formProperties.ownDescription')}</p>

      <ul
        className="space-y-2"
        aria-label={t('task.formProperties.listLabel')}
        onDragOver={(e) => {
          if (e.dataTransfer.types.includes(ROW_DRAG_TYPE)) e.preventDefault();
        }}
        onDrop={(e) => {
          const raw = e.dataTransfer.getData(ROW_DRAG_TYPE);
          if (raw === '') return;
          e.preventDefault();
          const from = Number(raw);
          const to = dropIndex ?? value.length;
          // Removing the row first shifts everything after it, so a drop past
          // the origin has to lose one to land where the line was drawn.
          move(from, to > from ? to - 1 : to);
          setDropIndex(null);
        }}
      >
        {value.map((row, index) => {
          const key = formPropertyRowKey(row) || `row-${index}`;
          const property = row.custom_field_id ? null : (row.property as TaskFormProperty | null);
          const missingField =
            !!row.custom_field_id &&
            !customFieldOptions.some((option) => option.value === row.custom_field_id);
          return (
            <li
              key={key}
              draggable={!disabled}
              onDragStart={(e) => {
                e.dataTransfer.setData(ROW_DRAG_TYPE, String(index));
                e.dataTransfer.effectAllowed = 'move';
              }}
              onDragOver={(e) => {
                if (!e.dataTransfer.types.includes(ROW_DRAG_TYPE)) return;
                e.preventDefault();
                const box = e.currentTarget.getBoundingClientRect();
                setDropIndex(e.clientY < box.top + box.height / 2 ? index : index + 1);
              }}
              onDragEnd={() => setDropIndex(null)}
              className={`flex flex-wrap items-center gap-2 rounded-md border bg-background px-3 py-2 ${
                row.hidden ? 'border-dashed border-border' : 'border-border'
              } ${dropIndex === index ? 'border-t-2 border-t-primary' : ''} ${
                dropIndex === index + 1 ? 'border-b-2 border-b-primary' : ''
              }`}
            >
              <Glyph
                path={GRIP}
                className={`h-4 w-4 shrink-0 text-muted-foreground ${disabled ? '' : 'cursor-grab'}`}
              />
              <span className="w-5 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
                {index + 1}
              </span>
              <span
                className={`truncate text-sm font-medium ${
                  row.hidden ? 'text-muted-foreground line-through' : 'text-foreground'
                }`}
              >
                {rowLabel(row)}
              </span>
              {row.custom_field_id && (
                <Badge variant="secondary" size="sm">
                  {t('task.formProperties.customField')}
                </Badge>
              )}
              {/* A field the catalogue no longer has cannot be drawn on a form.
                  Saying so beside it is the only way the row is fixable. */}
              {missingField && (
                <span className="text-xs text-destructive">
                  {t('task.formProperties.unknownField')}
                </span>
              )}
              {property && !CREATE_FORM_PROPERTIES.has(property) && (
                <span className="text-xs text-muted-foreground/80">
                  {t('task.formProperties.notOnCreate')}
                </span>
              )}

              <div className="ml-auto flex shrink-0 flex-wrap items-center gap-3">
                <ToggleSwitch
                  label={t('task.formProperties.shown')}
                  checked={!row.hidden}
                  disabled={disabled || property === 'TASK_FORM_PROPERTY_TYPE'}
                  onChange={(shown) => onChange(updateFormProperty(value, index, { hidden: !shown }))}
                  className="gap-2"
                />
                <ToggleSwitch
                  label={t('task.formProperties.required')}
                  checked={!!row.required}
                  // Required and hidden together is a form nobody can submit.
                  // The switch is out of reach rather than the save being
                  // refused after the fact.
                  disabled={disabled || !!row.hidden}
                  onChange={(required) => onChange(updateFormProperty(value, index, { required }))}
                  className="gap-2"
                />
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={disabled || index === 0}
                  aria-label={t('task.formProperties.moveUp', { name: rowLabel(row) })}
                  title={t('task.formProperties.moveUp', { name: rowLabel(row) })}
                  onClick={() => move(index, index - 1)}
                >
                  <Glyph path={CHEVRON_UP} />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={disabled || index === value.length - 1}
                  aria-label={t('task.formProperties.moveDown', { name: rowLabel(row) })}
                  title={t('task.formProperties.moveDown', { name: rowLabel(row) })}
                  onClick={() => move(index, index + 1)}
                >
                  <Glyph path={CHEVRON_DOWN} />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-destructive"
                  disabled={disabled || property === 'TASK_FORM_PROPERTY_TYPE'}
                  onClick={() => onChange(value.filter((_, i) => i !== index))}
                >
                  {t('common.remove')}
                </Button>
              </div>
            </li>
          );
        })}
      </ul>

      {/* The type row survives every configuration — the forms put it back if it
          is missing — so the editor says that rather than letting it be removed
          and quietly reappear. */}
      <p className="text-xs text-muted-foreground">{t('task.formProperties.typeAlwaysShown')}</p>

      <div className="flex flex-wrap items-center gap-3">
        <Button
          size="sm"
          variant="secondary"
          disabled={disabled || (missingProperties.length === 0 && availableCustomFields.length === 0)}
          onClick={(e) => setAddAnchor(e.currentTarget.getBoundingClientRect())}
        >
          {t('task.formProperties.add')}
        </Button>
        <Button size="sm" variant="ghost" disabled={disabled} onClick={() => onChange([])}>
          {t('task.formProperties.reset')}
        </Button>
        <p className="text-xs text-muted-foreground">{t('task.formProperties.resetHint')}</p>
      </div>

      {addAnchor && (
      <AnchoredMenu
        anchor={addAnchor}
        open
        onClose={() => setAddAnchor(null)}
        ariaLabel={t('task.formProperties.add')}
        width={280}
        maxHeight={340}
        className="py-1"
      >
        {missingProperties.length > 0 && (
          <>
            <div className="px-3 pb-1 pt-1.5 text-xs font-medium text-muted-foreground">
              {t('task.formProperties.builtIn')}
            </div>
            {missingProperties.map((property) => (
              <button
                key={property}
                type="button"
                onClick={() =>
                  append({ property, custom_field_id: '', required: false, hidden: false })
                }
                className="flex w-full items-center px-3 py-1.5 text-left text-sm text-foreground hover:bg-accent focus:bg-accent focus:outline-none"
              >
                {t(formPropertyLabelKey(property))}
              </button>
            ))}
          </>
        )}
        {availableCustomFields.length > 0 && (
          <>
            <div className="px-3 pb-1 pt-1.5 text-xs font-medium text-muted-foreground">
              {t('task.customFields')}
            </div>
            {availableCustomFields.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() =>
                  append({
                    custom_field_id: option.value,
                    required: false,
                    hidden: false,
                  } as TaskTypeProperty)
                }
                className="flex w-full items-center px-3 py-1.5 text-left text-sm text-foreground hover:bg-accent focus:bg-accent focus:outline-none"
              >
                {option.label}
              </button>
            ))}
          </>
        )}
      </AnchoredMenu>
      )}
    </div>
  );
}

export default TaskTypeFormPropertiesEditor;
